/**
 * Business events for the record-access dimension scope (DP-3188 — PRD §9).
 *
 * Three events, and between them they answer the two questions §9 asks: does
 * the first-login picker convert (`picker_shown` → `picker_committed`), and how
 * often do actors re-scope themselves afterwards (`sidebar_committed`)?
 *
 * They ride the observability client the dashboard already initialises in
 * `o11yUtils` and hands to `UserContext` — no new dependency, and the same
 * `trackEvent` shape as `app_initialized` / `user_logged_out`.
 *
 * **Origin is in the event name, not in a payload field.** So a commit reports
 * as exactly one of the two: the sidebar's own commits emit
 * `sidebar_committed`, and the gate's instance opts out
 * (`emitSidebarCommits: false`) because none of its writes is a sidebar
 * interaction — hydration and the forced single-value choice are not user
 * commits at all, and the picker's commit already reports itself here. Without
 * that split, every page load with a stored scope would land in the
 * "re-scoped from the sidebar" bucket.
 *
 * **Every emit is fire-and-forget.** No `await`, no return value, no throw: a
 * metric has no user-facing failure path, so a missing or broken client is a
 * silent no-op rather than something a commit has to handle.
 */

import { getObservabilityClient } from '@locus-taxy/encore-observability/observability';

import { O11yEventName } from './o11yUtils';

/** Who is committing, and against which dimension — on all three events. */
interface DimensionScopeActor {
  /** `activeDimension.name`. Backend-driven, so never `t()`-wrapped. */
  dimensionName: string;
  clientId: string;
  /** Personnel entity id — the same id the storage key is built from. */
  userId: string;
}

/**
 * How much of the dimension a commit covers.
 *
 * `'all'` is the sentinel form — paramless URL plus `__ALL__` in storage — so
 * its `size` is the actor's whole reach rather than however many rows happened
 * to be ticked to get there. `'clear'` is the empty commit, which
 * `useDimensionSelection` normalises to "no stored choice"; it is unreachable
 * from the picker, whose answer must name at least one value.
 */
export type DimensionScopeCommitKind = 'partial' | 'all' | 'clear';

function track(name: O11yEventName, attributes: Record<string, unknown>): void {
  try {
    // `undefined` whenever RUM was never initialised — dev builds, or a
    // blocked ingestion endpoint. That is the normal case, not an error.
    getObservabilityClient()?.trackEvent({ name, attributes });
  } catch {
    // Deliberately swallowed: see the fire-and-forget note above.
  }
}

/** The gate decided to ask. Emitted once per entry into `{ kind: 'picker' }`. */
export function trackDimensionPickerShown({
  dimensionName,
  clientId,
  userId,
  optionCount,
}: DimensionScopeActor & {
  /** The actor's whole reach — the denominator for "how many did they pick?". */
  optionCount: number;
}): void {
  track(O11yEventName.DimensionScopePickerShown, {
    dimensionName,
    clientId,
    userId,
    optionCount,
  });
}

/** The actor answered the first-login picker. Never `'clear'` — see the kind. */
export function trackDimensionPickerCommitted({
  dimensionName,
  clientId,
  userId,
  kind,
  size,
}: DimensionScopeActor & {
  kind: Exclude<DimensionScopeCommitKind, 'clear'>;
  size: number;
}): void {
  track(O11yEventName.DimensionScopePickerCommitted, {
    dimensionName,
    clientId,
    userId,
    kind,
    size,
  });
}

/** The actor re-scoped from the sidebar's `RecordAccessSelector`. */
export function trackDimensionSidebarCommitted({
  dimensionName,
  clientId,
  userId,
  kind,
  size,
}: DimensionScopeActor & {
  kind: DimensionScopeCommitKind;
  size: number;
}): void {
  track(O11yEventName.DimensionScopeSidebarCommitted, {
    dimensionName,
    clientId,
    userId,
    kind,
    size,
  });
}
